export type ShareLanguage = "lotin" | "kirill" | "yangilotin";

export interface ShareTranslationRequest {
  original_text: string;
  translated_text: string;
  from_language: ShareLanguage;
  to_language: ShareLanguage;
}

export interface ShareTranslationResponse {
  success: boolean;
  data?: {
    share_code: string;
    share_url: string;
    expires_at: string;
  };
  message?: string;
  errors?: Record<string, string[]>;
}

export interface GetShareResponse {
  success: boolean;
  data?: {
    original_text: string;
    translated_text: string;
    from_language: string;
    to_language: string;
    views_count: number;
    created_at: string;
  };
  message?: string;
}

const withBase = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `/api${normalizedPath}`;
};

export async function createShare(
  data: ShareTranslationRequest
): Promise<ShareTranslationResponse> {
  try {
    const response = await fetch(withBase("/translations/share"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        original_text: data.original_text,
        translated_text: data.translated_text,
        from_language: data.from_language,
        to_language: data.to_language,
      }),
    });

    const result: ShareTranslationResponse = await response.json();

    if (!response.ok) {
      if (response.status === 422) {
        throw new Error(result.message || "Validation xatosi");
      }
      throw new Error(result.message || "Share yaratishda xatolik");
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Share yaratishda xatolik yuz berdi",
    };
  }
}

export async function getShareByCode(
  shareCode: string
): Promise<GetShareResponse> {
  try {
    const response = await fetch(withBase(`/translations/${shareCode}`), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    });

    const result: GetShareResponse = await response.json();

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Share topilmadi yoki muddati tugagan");
      }
      throw new Error(result.message || "Ma'lumot olishda xatolik");
    }

    return result;
  } catch (error) {
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Ma'lumot olishda xatolik yuz berdi",
    };
  }
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  } catch (error) {
    console.error("Copy to clipboard failed:", error);
    return false;
  }
}
